import React from 'react';
import {
  ArrowForwardIcon,
  Button,
  CheckCircleOutlineIcon,
  PageHeader,
  Spinner,
  Text,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../../../routes';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useCompleteOnboarding } from '../hooks/useCompleteOnboarding';
import { useMultiAccountStore } from '../../auth/stores/multiAccountStore';
import {
  getCompletedCount,
  isAllComplete,
  TOTAL_STEPS,
} from '../utils/onboardingSteps';

const PAGE_KEYFRAMES = `
@keyframes gspg-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
`;

const GettingStartedPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: status, isLoading } = useOnboardingStatus();
  const { mutate: complete, isPending } = useCompleteOnboarding();
  const orgName = useMultiAccountStore(
    s => s.getCurrentAccount()?.currentOrganisationName
  );

  const completedSteps = status ? getCompletedCount(status) : 0;
  const totalSteps = TOTAL_STEPS;
  const allComplete = status ? isAllComplete(status) : false;

  const progressValue = (completedSteps / totalSteps) * 100;
  const ringSize = 44;
  const ringStroke = 3;
  const ringRadius = (ringSize - ringStroke) / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - progressValue / 100);

  return (
    <div className="flex h-full flex-col">
      <style>{PAGE_KEYFRAMES}</style>
      <PageHeader
        title="Getting Started"
        data-testid="getting-started-header"
      />

      <div className="flex flex-1 justify-center overflow-auto">
        <div className="w-full max-w-[600px] px-4 py-5 sm:px-6 sm:py-8">
          {/* Welcome + progress */}
          <div
            className="mb-6 animate-[gspg-fade-in_0.5s_ease-out_0.1s_both] sm:mb-8"
            data-testid="getting-started-welcome"
          >
            <div className="mb-2 flex items-center gap-4">
              <Text
                as="h2"
                variant="h5"
                className="flex-1 font-bold tracking-tight"
              >
                Welcome{orgName ? `, ${orgName}` : ''}!
              </Text>

              {/* Circular progress ring */}
              {status && (
                <div
                  className="relative inline-flex"
                  data-testid="getting-started-progress"
                >
                  <svg
                    width={ringSize}
                    height={ringSize}
                    viewBox={`0 0 ${ringSize} ${ringSize}`}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={totalSteps}
                    aria-valuenow={completedSteps}
                  >
                    <circle
                      cx={ringSize / 2}
                      cy={ringSize / 2}
                      r={ringRadius}
                      fill="none"
                      strokeWidth={ringStroke}
                      className="stroke-surface-selected"
                    />
                    <circle
                      cx={ringSize / 2}
                      cy={ringSize / 2}
                      r={ringRadius}
                      fill="none"
                      strokeWidth={ringStroke}
                      strokeLinecap="round"
                      className={
                        allComplete
                          ? 'stroke-surface-success'
                          : 'stroke-surface-primary'
                      }
                      strokeDasharray={ringCircumference}
                      strokeDashoffset={ringOffset}
                      transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Text
                      as="span"
                      variant="caption"
                      className={`text-[0.7rem] font-bold ${
                        allComplete
                          ? 'text-content-success'
                          : 'text-content-primary'
                      }`}
                    >
                      {completedSteps}/{totalSteps}
                    </Text>
                  </div>
                </div>
              )}
            </div>

            <Text
              as="div"
              variant="body2"
              color="secondary"
              className="max-w-[440px] leading-relaxed"
            >
              Complete these steps to get your organisation set up and ready to
              go.
            </Text>
          </div>

          {isLoading ? (
            <div
              className="flex justify-center py-12"
              data-testid="getting-started-loading"
            >
              <Spinner />
            </div>
          ) : status ? (
            <>
              <OnboardingChecklist status={status} />

              <div className="mt-8 flex animate-[gsob-fade-slide-up_0.4s_ease-out_0.9s_both] justify-center">
                <Button
                  variant={allComplete ? 'neutral' : 'ghost'}
                  className={allComplete ? undefined : 'text-content-secondary'}
                  onClick={() =>
                    complete(undefined, {
                      onSuccess: () =>
                        navigate(Routes.sites.manage, { replace: true }),
                    })
                  }
                  disabled={isPending}
                  data-testid="onboarding-dismiss-button"
                >
                  {isPending
                    ? 'Completing...'
                    : allComplete
                      ? 'All done — go to my sites'
                      : 'Skip for now'}
                  {allComplete ? (
                    <CheckCircleOutlineIcon
                      aria-hidden="true"
                      className="size-[18px]"
                    />
                  ) : (
                    <ArrowForwardIcon aria-hidden="true" className="size-4" />
                  )}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPage;
